<template>
    <el-dialog
        :visible="visible"
        :title="dialogTitle"
        width="540px"
        custom-class="ro-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="close"
        @closed="onClosed"
    >
        <div v-if="order" class="ro-assign">
            <div class="ro-assign__order">
                <div class="ro-assign__code">#{{ order.id }}</div>
                <div class="ro-assign__title">
                    {{ order.title || order.position_title || "Yêu cầu tuyển dụng" }}
                    <span v-if="order.quantity">· SL: {{ order.quantity }}</span>
                </div>
                <div class="ro-assign__sub">
                    <span v-if="departmentName">Phòng ban: <b>{{ departmentName }}</b> · </span>
                    <span>Người tạo: <b>{{ creatorName }}</b></span>
                    <span v-if="order.coordinator_id || (order.coordinator && order.coordinator.name)">
                        · Điều phối: <b>{{ (order.coordinator && order.coordinator.name) || (order.coordinator_id === 332991 ? 'Chị Cẩm Tú' : `ID #${order.coordinator_id}`) }}</b>
                    </span>
                </div>
                <div class="ro-assign__current">
                    <span class="ro-assign__current-label">Phụ trách hiện tại:</span>
                    <span v-if="currentPersonInChargeName" class="ro-assign__current-val">
                        <i class="el-icon-user-solid"></i> {{ currentPersonInChargeName }}
                    </span>
                    <span v-else class="ro-assign__current-empty">Chưa phân công</span>
                </div>
            </div>

            <el-form label-position="top" size="small" class="ro-assign__form">
                <el-form-item :label="isTeacherOrder ? 'Trưởng nhóm phụ trách' : 'HR phụ trách'">
                    <el-select
                        v-model="selectedUserId"
                        filterable
                        clearable
                        :loading="loading"
                        :placeholder="isTeacherOrder ? 'Chọn trưởng nhóm phụ trách...' : 'Chọn HR phụ trách hoặc nhập để tìm...'"
                        class="w-100"
                    >
                        <el-option
                            v-for="u in userOptions"
                            :key="u.id"
                            :label="userOptionLabel(u)"
                            :value="u.id"
                        >
                            <div class="ro-user-opt">
                                <span class="ro-user-opt__name">{{ u.name }}</span>
                                <span v-if="u.department_name || u.team_name || u.email" class="ro-user-opt__sub">
                                    {{ u.department_name || u.team_name || u.email }}
                                </span>
                            </div>
                        </el-option>
                    </el-select>
                    <div class="ro-assign__help">
                        <i class="el-icon-info"></i>
                        {{ isTeacherOrder
                            ? 'Trưởng nhóm được gán sẽ chịu trách nhiệm tiếp nhận yêu cầu tuyển hộ và tìm kiếm, điều phối ứng viên giáo viên.'
                            : 'Nhân sự được gán sẽ chịu trách nhiệm tiếp nhận, tìm kiếm và đẩy hồ sơ ứng viên cho yêu cầu này.' }}
                    </div>
                </el-form-item>
            </el-form>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button size="small" @click="close">Huỷ</el-button>
            <el-button
                size="small"
                :type="selectedUserId ? 'primary' : 'warning'"
                :loading="submitting"
                @click="submit"
            >
                {{ selectedUserId ? "Xác nhận gán" : "Lưu (Bỏ gán)" }}
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import {
    RO_GET_HR_TEAMLEADS,
    RO_GET_TEACHER_GROUPS,
} from "@/core/services/store/recruitment-orders/recruitmentOrders.module";

export default {
    name: "AssignPersonInChargeDialog",
    props: {
        visible: { type: Boolean, default: false },
        order: { type: Object, default: null },
        submitting: { type: Boolean, default: false },
        hrTeamLeads: { type: Array, default: () => [] },
        isHrDepartment: { type: Boolean, default: false },
        canAssignPersonInCharge: { type: Boolean, default: true },
        isCoordinator: { type: Boolean, default: false },
        currentUserId: { type: [Number, String], default: null },
    },
    data() {
        return {
            selectedUserId: null,
            userOptions: [],
            loading: false,
        };
    },
    computed: {
        ...mapGetters(["roHrTeamLeads"]),
        isTeacherOrder() {
            return !!(this.order && (Number(this.order.type) === 2 || this.order.type === "teacher"));
        },
        dialogTitle() {
            return this.isTeacherOrder ? "Gán Trưởng nhóm phụ trách" : "Gán người phụ trách";
        },
        departmentName() {
            if (!this.order) return "";
            if (this.order.department && this.order.department.name) return this.order.department.name;
            return this.order.department_name || "";
        },
        creatorName() {
            if (!this.order) return "—";
            if (this.order.created_user_info && this.order.created_user_info.name) return this.order.created_user_info.name;
            return this.order.created_by_name || (this.order.created_user ? `ID ${this.order.created_user}` : "—");
        },
        currentPersonInChargeName() {
            if (!this.order) return "";
            if (this.order.person_in_charge && this.order.person_in_charge.name) return this.order.person_in_charge.name;
            return this.order.assigned_hr_teamlead_name || "";
        },
    },
    watch: {
        visible(val) {
            if (val && this.order) {
                this.initData();
            }
        },
        hrTeamLeads: {
            deep: true,
            handler(newVal) {
                if (!this.isTeacherOrder && Array.isArray(newVal) && newVal.length > 0) {
                    this.populateUserOptions(newVal);
                }
            },
        },
        roHrTeamLeads: {
            deep: true,
            handler(newVal) {
                if (!this.isTeacherOrder && Array.isArray(newVal) && newVal.length > 0) {
                    this.populateUserOptions(newVal);
                }
            },
        },
    },
    methods: {
        initData() {
            const currentId = this.order.person_in_charge_id || (this.order.person_in_charge && this.order.person_in_charge.id) || null;
            this.selectedUserId = currentId ? Number(currentId) : null;
            if (this.isTeacherOrder) {
                this.loadTeacherGroups();
            } else {
                this.loadHrTeam();
            }
        },
        loadTeacherGroups() {
            this.loading = true;
            this.$store
                .dispatch(RO_GET_TEACHER_GROUPS, {})
                .then((list) => {
                    const results = Array.isArray(list) ? list : [];
                    if (this.order && this.order.person_in_charge && this.order.person_in_charge.id) {
                        if (!results.some((u) => String(u.id) === String(this.order.person_in_charge.id))) {
                            results.unshift(this.order.person_in_charge);
                        }
                    }
                    this.userOptions = results;
                })
                .catch(() => {
                    this.userOptions = [];
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadHrTeam() {
            const sourceList = (Array.isArray(this.hrTeamLeads) && this.hrTeamLeads.length > 0)
                ? this.hrTeamLeads
                : (Array.isArray(this.roHrTeamLeads) && this.roHrTeamLeads.length > 0)
                ? this.roHrTeamLeads
                : [];

            if (sourceList.length > 0) {
                this.populateUserOptions(sourceList);
                return;
            }

            this.loading = true;
            this.$store
                .dispatch(RO_GET_HR_TEAMLEADS)
                .then((list) => {
                    const results = Array.isArray(list) ? list : (this.roHrTeamLeads || []);
                    this.populateUserOptions(results);
                })
                .catch(() => {
                    this.populateUserOptions(this.hrTeamLeads || this.roHrTeamLeads || []);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        populateUserOptions(hrList = []) {
            let list = Array.isArray(hrList) ? [...hrList] : [];
            // Lọc: nếu user có thông tin account_type_id hoặc department_id thì chỉ lấy account_type_id = 16 của department_id = 3
            list = list.filter((u) => {
                if (!u) return false;
                const accId = u.account_type_id || (u.account_type && u.account_type.id);
                if (accId !== undefined && accId !== null && Number(accId) !== 16) return false;
                const deptId = u.department_id || (u.department && u.department.id);
                if (deptId !== undefined && deptId !== null && Number(deptId) !== 3) return false;
                return true;
            });
            if (this.order) {
                const pic = this.order.person_in_charge;
                const picId = this.order.person_in_charge_id || (pic && pic.id);
                if (pic && pic.id) {
                    if (!list.some((u) => String(u.id) === String(pic.id))) {
                        list.unshift(pic);
                    }
                } else if (picId && this.order.person_in_charge_name) {
                    if (!list.some((u) => String(u.id) === String(picId))) {
                        list.unshift({
                            id: Number(picId),
                            name: this.order.person_in_charge_name,
                        });
                    }
                }
            }
            this.userOptions = list;
        },
        userOptionLabel(u) {
            if (!u) return "";
            const name = u.name || `User #${u.id}`;
            const extra = u.department_name || u.team_name || u.email;
            return extra ? `${name} (${extra})` : name;
        },
        close() {
            this.$emit("update:visible", false);
        },
        onClosed() {
            this.selectedUserId = null;
        },
        submit() {
            if (!this.order) return;
            const canAssign =
                this.canAssignPersonInCharge ||
                this.isCoordinator ||
                (this.order.coordinator_id && Number(this.order.coordinator_id) === Number(this.currentUserId));
            if (!canAssign) {
                this.$message.warning("Bạn không có quyền phân công người phụ trách cho yêu cầu này");
                return;
            }
            const selectedObj = this.userOptions.find((u) => String(u.id) === String(this.selectedUserId)) || null;
            this.$emit("submit", {
                id: this.order.id,
                person_in_charge_id: this.selectedUserId || null,
                person_in_charge: selectedObj,
            });
        },
    },
};
</script>

<style scoped>
.ro-assign__order {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 16px;
}
.ro-assign__code {
    font-size: 12px;
    font-weight: 700;
    color: #4f46e5;
    margin-bottom: 2px;
}
.ro-assign__title {
    font-size: 14.5px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1.4;
}
.ro-assign__title span {
    font-weight: 500;
    color: #64748b;
    font-size: 13px;
}
.ro-assign__sub {
    font-size: 12.5px;
    color: #64748b;
    margin-top: 6px;
}
.ro-assign__current {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #e2e8f0;
    font-size: 12.5px;
    display: flex;
    align-items: center;
    gap: 6px;
}
.ro-assign__current-label {
    color: #64748b;
    font-weight: 500;
}
.ro-assign__current-val {
    color: #059669;
    font-weight: 600;
}
.ro-assign__current-empty {
    color: #94a3b8;
    font-style: italic;
}
.ro-assign__form {
    margin-top: 8px;
}
.ro-assign__help {
    font-size: 12px;
    color: #64748b;
    margin-top: 6px;
    line-height: 1.4;
}
.ro-assign__help i {
    color: #4f46e5;
}
.ro-user-opt {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}
.ro-user-opt__name {
    font-weight: 600;
    color: #1e293b;
}
.ro-user-opt__sub {
    font-size: 12px;
    color: #94a3b8;
}
</style>
